import { Permission, ReportList, Permissions, ListPagination, LazyLoadAutoCompleteInput } from "../../components";
import { makeStyles } from "@mui/styles";
import { PERMISSIONS } from "../../constants";
import { Datagrid, DateField, DateInput, Filter, NumberField, ReferenceField, ReferenceInput, SelectInput, TextField, useListContext } from "react-admin";
import moment from "moment";
import { UserRole } from "../../providers/authProvider";
import { Box, Typography } from "@mui/material";
import { formatPrice } from "../../utils/utils";
import { FillinReviewStatus } from "../../models";

const useStyles = makeStyles(theme => ({
  title: {
    margin: '10px 0 10px 0',
    textAlign: 'center',
    fontWeight: 'bold',
    paddingTop: 30
  },
  headerCell: {
    backgroundColor: '#E5E5E5',
  },
}));

const ReportMessage = ({ title }) => (
  <Box sx={{ padding: 2 }}>
    <Typography variant="subtitle1" textAlign="center">{title}</Typography>
  </Box>
);

const FiltersReportList = () => {
  return (
    <Filter>
      <DateInput source="from" label="De" sx={{ marginBottom: 0.5 }} options={{ format: 'DD/MM/YYYY', disableFuture: true }} alwaysOn parse={(value) => moment(value).startOf('day').toISOString()} />
      <DateInput source="to" label="Até" sx={{ marginBottom: 0.5 }} options={{ format: 'DD/MM/YYYY', disableFuture: true }} alwaysOn parse={(value) => moment(value).endOf('day').toISOString()} />
      {
        Permission({
          userRole: PERMISSIONS.ADMIN,
          children: (
            <ReferenceInput
              label="Grupo de Transportadora"
              source="companyId"
              reference="companies"
              sort={{ field: "name", order: "ASC" }}
              style={{ width: 225 }}
              alwaysOn
            >
              <SelectInput
                label="Grupo de Transportadora"
                optionText={'name'}
                emptyText="ra.action.clear_input_value"
              />
            </ReferenceInput>
          )
        })
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.company],
          children: (
            <ReferenceInput
              label="Rede"
              source="chainId"
              reference="chains"
              sort={{ field: "name", order: "ASC" }}
              alwaysOn
            >
              <SelectInput
                label="Rede"
                optionText={'name'}
                emptyText="ra.action.clear_input_value"
              />
            </ReferenceInput>
          )
        })
      }
      <ReferenceInput label="Produto" source="fuelId" reference="fuels" sort={{ field: "name", order: "ASC" }} alwaysOn>
        <SelectInput
          emptyText="Todos"
          label="Produto"
          optionText={'name'}
          isRequired
        />
      </ReferenceInput>
      <LazyLoadAutoCompleteInput
        label="Placa do Veículo"
        source="licensePlate"
        format={choice => choice.licensePlate}
        style={{ minWidth: 220 }}
        reference="vehicles"
        alwaysOn
      />
    </Filter>
  );
}


const FilterTablePrint = () => {
  const classes = useStyles();
  const { filterValues } = useListContext();
  return (
    <Datagrid classes={{ headerCell: classes.headerCell }} data={[{ ...filterValues, generatedReportAt: new Date() }]} total={1} bulkActionButtons={false}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      {
        Permission({
          userRole: PERMISSIONS.ADMIN,
          children: (
            <ReferenceField
              label="Grupo de Transportadora"
              source="companyId"
              reference="companies"
              sort={{ field: "name", order: "ASC" }}
              emptyText="Todos"
              style={{ width: 225 }}
              alwaysOn
            >
              <TextField source="name" />
            </ReferenceField>
          )
        })
      }
      {
        Permissions({
          userRoles: [UserRole.admin, UserRole.company],
          children: (
            <ReferenceField
              label="Rede"
              source="chainId"
              reference="chains"
              sort={{ field: "name", order: "ASC" }}
              emptyText="Todos"
              alwaysOn
            >
              <TextField
                source="name"
              />
            </ReferenceField>
          )
        })
      }
      <ReferenceField label="Produto" emptyText="Todos" source="fuelId" textAlign="center" reference="fuels" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <TextField label="Placa" source="licensePlate" />
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  )
}

const ListFillinsLicense = () => {
  const props = useListContext();
  const classes = useStyles();
  let data = [];
  let summaryData = [];

  if (!props.data) { return <></>; }

  if (props.data[0] && props.data[0].error) {
    return (<ReportMessage title={props.data[0].error} />);
  }

  if (props.data) {
    const fillinInfo = props.data[0];
    if (fillinInfo) {
      data = fillinInfo.data;
      summaryData = [fillinInfo.summaryData];
    }
  }

  return (
    <>
      <Typography className={classes.title}>Total</Typography>
      <Datagrid classes={{ headerCell: classes.headerCell }} style={{ marginBottom: 60 }} data={summaryData} total={1} bulkActionButtons={false}>
        <TextField
          source="total_fillins"
          textAlign="center"
          label="Total de abastecimentos"
          sortable={false}
        />
        <NumberField
          source="average_amount"
          label="Volume Médio"
          textAlign="center"
          emptyText="0"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <NumberField
          source="total_amount"
          label="Total em Litros"
          textAlign="center"
          emptyText="0"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <NumberField
          source="average_ticket"
          label="Ticket Médio"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="total_value"
          label="Total em Dinheiro"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
      </Datagrid>
      <Typography className={classes.title}>Abastecimentos Agrupados por Placa</Typography>
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        expand={false}
        data={data}
        classes={{ headerCell: classes.headerCell }}
      >
        <ReferenceField source="vehicle_id" reference="vehicles" label="Placa" sortable={false} link={false}>
          <TextField source="licensePlate" label="Placa" />
        </ReferenceField>
        <NumberField
          source="total_km"
          label="Total de Km rodado"
          locales="pt-BR"
          textAlign="center"
          emptyText="--"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <NumberField
          source="total_amount"
          label="Volume Total (litros)"
          textAlign="center"
          emptyText="--"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <NumberField
          source="total_value"
          label="Total em Reais"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <ReferenceField source="vehicle_id" reference="vehicles" label="Média Ideal (Km/l)" textAlign="center" sortable={false} link={false}>
          <TextField source="expectedAverage" emptyText="--" />
        </ReferenceField>
        <NumberField
          source="vehicleAverage"
          label="Média Real (Km/l)"
          textAlign="center"
          sortable={false}
          emptyText="--"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
      </Datagrid>
    </>
  )
}

const reportExporter = async (data, fetchRelatedRecords) => {
  let licensePlateInfo = data[0].data;

  const relations = [
    { field: 'vehicle_id', resource: 'vehicles' },
  ];

  const fetchData = await fetchRelatedRecords(relations, licensePlateInfo);

  const getData = (resource) => {
    return fetchData.find(({ resource: res }) => res === resource)?.results || [];
  }

  return licensePlateInfo.map(licensePlate => {
    const { vehicle_id, total_km, total_amount, total_value, vehicleAverage } = licensePlate;
    const vehicle = getData('vehicles').find(({ id }) => id === vehicle_id);
    return {
      placa: vehicle?.licensePlate,
      total_de_km_rodado: total_km,
      media_real_km_l: vehicleAverage,
      media_ideal_km_l: vehicle?.expectedAverage,
      volume_total: total_amount,
      total_em_reais: formatPrice(total_value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    }
  });
}

const LicensePlateReport = () => {
  return (
    <Permission permission={PERMISSIONS.REPORT_FILLIN_LICENSE}>
      <ReportList
        title="Relatório de Abastecimentos por Placa"
        resource="reports/fillin-license"
        filters={<FiltersReportList />}
        filter={{ stationReviewStatus: FillinReviewStatus.approved }}
        filterDefaultValues={{
          from: moment().startOf('day').subtract(1, 'month').toISOString(),
          to: moment().endOf('day').toISOString(),
        }}
        pagination={<ListPagination />}
        bulkActionButtons={false}
        perPage={25}
        fileName="abastecimentos_por_placa"
        customExporter={reportExporter}
        titleOnPrint="Relatório de Abastecimentos por Placa"
        filterTablePrint={<FilterTablePrint />}
        landscape={true}
        hasSummary={false}
      >
        <ListFillinsLicense />
      </ReportList>
    </Permission>
  );
};

export default LicensePlateReport;